import { isJointPolicy } from '../funding/calcFundingSourceDerivatives';
import type { ComparisonScenario } from './types';

/**
 * True if the derived primary has an active joint policy AND a partner
 * was supplied. A joint quote without a partner silently falls back to
 * single mode.
 */
export function isCoupleScenario(derivedPrimary: Client, derivedPartner: Client | null): boolean {
  if (!derivedPartner) return false;

  const policies = derivedPrimary.multipleFundingSources?.ltcPolicy ?? {};

  for (const policy of Object.values(policies)) {
    if (policy.isDeactivated) continue;
    if (isJointPolicy(policy.policyType, derivedPartner)) return true;
  }

  return false;
}

/**
 * Sum a numeric field across the partner's active, non-joint policies.
 *
 * Use this for POLICY-WIDE values — total value, face amount, non-LTC payout.
 * The projection adapter writes those from the full joint projection onto BOTH
 * claimants' copies, so adding the partner's would double-count. Partner-only
 * standalone policies (e.g. a single-life policy held before the joint quote)
 * live only on the partner side and must be added back.
 *
 * PER-CLAIMANT values (coverage) need `sumPartnerJointPolicies` as well — see
 * there.
 */
export function sumPartnerNonJointPolicies(
  partner: Client | null,
  getValue: (policy: PolicyFundingSource) => number | null | undefined,
): number {
  if (!partner) return 0;

  let total = 0;

  const policies = partner.multipleFundingSources?.ltcPolicy ?? {};

  for (const policy of Object.values(policies)) {
    if (policy.isDeactivated) continue;
    if (isJointPolicy(policy.policyType, partner)) continue;
    total += getValue(policy) ?? 0;
  }

  return total;
}

/**
 * Sum a numeric field across the partner's active JOINT policies.
 *
 * Needed only for per-claimant values. `projectionToLegacy` splits a joint
 * projection's claimant-tagged months and writes each spouse's own coverage to
 * their own copy of the policy — primary's `projectedCostCoverage` is the
 * PRIMARY's share, not the couple's. The legacy joint accumulator did sum both
 * onto primary, which is why the couple totals here used to exclude the partner
 * side entirely; under the engine that silently drops the partner's half.
 *
 * Do NOT use this for policy-wide scalars (total value, face, non-LTC payout) —
 * those are written whole on both copies and would double.
 */
function sumPartnerJointPolicies(
  partner: Client | null,
  getValue: (policy: PolicyFundingSource) => number | null | undefined,
): number {
  if (!partner) return 0;

  let total = 0;

  const policies = partner.multipleFundingSources?.ltcPolicy ?? {};

  for (const policy of Object.values(policies)) {
    if (policy.isDeactivated) continue;
    if (!isJointPolicy(policy.policyType, partner)) continue;
    total += getValue(policy) ?? 0;
  }

  return total;
}

function allPhaseTotalCost(client: Client | null): number {
  const costs = client?.allPhaseCosts;
  if (!costs) return 0;
  return (
    (costs.allPhaseInflatedProfessionalShareCost ?? 0) +
    (costs.allPhaseInflatedRoomAndBoardCost ?? 0)
  );
}

function sumActiveSelfFundingAssets(client: Client | null): number {
  if (!client) return 0;

  let total = 0;

  const sources = client.multipleFundingSources?.selfFunding ?? {};

  for (const source of Object.values(sources)) {
    if (!source.isDeactivated) total += source.existingAssetsValue ?? 0;
  }

  return total;
}

function sumActiveAnnuityCoverage(client: Client | null): number {
  if (!client) return 0;

  let total = 0;

  const sources = client.multipleFundingSources?.annuity ?? {};

  for (const annuity of Object.values(sources)) {
    if (annuity.isDeactivated) continue;
    total += annuity.annuityProjectedCostCoverage ?? 0;
  }

  return total;
}

function policyCoverageForClient(
  client: Client,
  policy: PolicyFundingSource,
): number | null | undefined {
  return client.intakeSurvey?.clientHasStartedLtc
    ? policy.policyCoverageOfRemainingLtcCosts
    : policy.projectedCostCoverage;
}

type AggregatedMetrics = Omit<
  ComparisonScenario,
  'isCoupleMode' | 'partnerFirstName' | '_derivedPrimary' | '_derivedPartner'
>;

/**
 * Build the scenario-level numeric aggregates from a pair of derived
 * clients. All sums are couple-aware: when `isCoupleMode` is true the
 * partner contributes (joint coverage from primary, non-joint coverage
 * added separately from partner side, self-funding and annuity summed
 * across both); otherwise the partner is ignored entirely.
 *
 * Reads only from `multipleFundingSources.*` and individual funding
 * source objects — never from the singular `fundingSources` working slot,
 * whose contents reflect the last engine iteration, not aggregates.
 */
export function aggregateMetrics(
  derivedPrimary: Client,
  derivedPartner: Client | null,
  isCoupleMode: boolean,
): AggregatedMetrics {
  const partner = isCoupleMode ? derivedPartner : null;

  // Care PLUS out-of-pocket room & board, matching the denominator in
  // `calcCombinedProjectedCostCoverage`. A care-only denominator here would sit
  // under a numerator that counts indemnity room & board, letting an
  // Independent Living policy read as fully covered when it is not. Room & board
  // is 0 for every other environment.
  const primaryLtcCost = allPhaseTotalCost(derivedPrimary);
  const partnerLtcCost = allPhaseTotalCost(partner);
  const projectedLtcCost = primaryLtcCost + partnerLtcCost;

  const selfFundingCoverage =
    (derivedPrimary.multipleFundingSources?.multipleSelfFundingProjectedCostCoverage ?? 0) +
    (partner?.multipleFundingSources?.multipleSelfFundingProjectedCostCoverage ?? 0);

  // Coverage is PER CLAIMANT under the projection engine: primary's aggregate
  // holds only the primary's share of a joint policy, so the partner's share has
  // to be added from their own copy. Partner's non-joint policies live only on
  // the partner side and are added the same way. Each policy is therefore
  // counted once per claimant that it actually covers, never twice.
  const getPartnerPolicyCoverage = (policy: PolicyFundingSource) =>
    partner ? policyCoverageForClient(partner, policy) : 0;
  const partnerNonJointPolicyCoverage = sumPartnerNonJointPolicies(partner, p =>
    getPartnerPolicyCoverage(p),
  );
  const partnerJointPolicyCoverage = sumPartnerJointPolicies(partner, p =>
    getPartnerPolicyCoverage(p),
  );
  // Indemnity cash applied to room & board is coverage of the client's total
  // cost, and the denominator below includes room & board — so it belongs in the
  // numerator, matching `calcCombinedProjectedCostCoverage`. 0 for reimbursement
  // and for every non-Independent-Living client.
  //
  // Summed per policy on the partner side for the same reason coverage is:
  // `calcMultiplePolicyTotalFields` runs only for the session client, so the
  // partner's `multiplePolicyRoomAndBoardCovered` aggregate is never recomputed
  // after the primary's pass mirrors a joint policy onto them. The per-policy
  // values are.
  const roomAndBoardCovered =
    (derivedPrimary.multipleFundingSources?.multiplePolicyRoomAndBoardCovered ?? 0) +
    sumPartnerNonJointPolicies(partner, p => p.policyRoomAndBoardCovered) +
    sumPartnerJointPolicies(partner, p => p.policyRoomAndBoardCovered);
  const policyCoverage =
    (derivedPrimary.multipleFundingSources?.multiplePolicyProjectedCostCoverage ?? 0) +
    partnerNonJointPolicyCoverage +
    partnerJointPolicyCoverage +
    roomAndBoardCovered;

  const annuityCoverage =
    sumActiveAnnuityCoverage(derivedPrimary) + sumActiveAnnuityCoverage(partner);

  const totalCoverage = selfFundingCoverage + policyCoverage + annuityCoverage;

  const coveragePercent =
    projectedLtcCost > 0 ? Math.round((totalCoverage / projectedLtcCost) * 100) : 0;
  const coverageGap = Math.max(0, projectedLtcCost - totalCoverage);
  const surplus = Math.max(0, totalCoverage - projectedLtcCost);

  // Current assets: existingAssetsValue across active self-funding (both
  // spouses) + active policy face values (joint face is on primary; add
  // partner's non-joint face values separately).
  let currentPolicyBenefit = 0;

  for (const policy of Object.values(derivedPrimary.multipleFundingSources?.ltcPolicy ?? {})) {
    if (!policy.isDeactivated) currentPolicyBenefit += policy.policyMaximumBenefitAmount ?? 0;
  }

  currentPolicyBenefit += sumPartnerNonJointPolicies(partner, p => p.policyMaximumBenefitAmount);
  const currentAssets =
    sumActiveSelfFundingAssets(derivedPrimary) +
    sumActiveSelfFundingAssets(partner) +
    currentPolicyBenefit;

  // Projected at start of LTC: self-funding total values + policy total
  // values. Annuities are excluded — the comparison is scoped to self-
  // funding vs self-funding+insurance.
  const selfFundingTotalValue =
    (derivedPrimary.multipleFundingSources?.multipleSelfFundingTotalValue ?? 0) +
    (partner?.multipleFundingSources?.multipleSelfFundingTotalValue ?? 0);
  const partnerNonJointPolicyTotalValue = sumPartnerNonJointPolicies(
    partner,
    p => p.policyTotalValue,
  );
  const policyTotalValue =
    (derivedPrimary.multipleFundingSources?.multiplePolicyTotalValue ?? 0) +
    partnerNonJointPolicyTotalValue;
  const projectedAssetsAtStart = selfFundingTotalValue + policyTotalValue;

  // Projected at end of LTC: policy pays first, self-funding covers the
  // remainder. Hybrid/life policies convert unused benefit to a death
  // benefit payout (`multiplePolicyNonLtcPayout`); traditional LTC
  // forfeits unused benefit (that field stays at 0).
  const excessPolicyCoverage = Math.max(0, policyCoverage - projectedLtcCost);
  const policyNonLtcPayout =
    (derivedPrimary.multipleFundingSources?.multiplePolicyNonLtcPayout ?? 0) +
    sumPartnerNonJointPolicies(partner, p => p.policyNonLtcPayout);
  // The projection adapter includes indemnity cash spent on room & board in
  // `policyNonLtcPayout` so policy returns remain whole. It is a return, but not
  // an asset remaining after care, so remove it here before reporting end assets.
  const unspentPolicyPayout = Math.max(0, policyNonLtcPayout - roomAndBoardCovered);
  const policyRemainder = unspentPolicyPayout + excessPolicyCoverage;
  const costAfterPolicy = Math.max(0, projectedLtcCost - policyCoverage);
  const selfFundingSurplus = Math.max(0, selfFundingTotalValue - costAfterPolicy);
  const projectedAssetsAtEnd = selfFundingSurplus + policyRemainder;

  return {
    projectedLtcCost,
    selfFundingCoverage,
    policyCoverage,
    annuityCoverage,
    totalCoverage,
    coveragePercent,
    coverageGap,
    surplus,
    currentAssets,
    projectedAssetsAtStart,
    projectedAssetsAtEnd,
  };
}
